import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchSelect, type SearchSelectOutcome } from "../shared/SearchSelect";
import { useToast } from "../toast/toastContext";
import {
  MinimumTeamSearchLength,
  raiseJoinTeamRequest,
  searchTeams,
  type TeamSearchResult,
} from "../../api/joinTeam";
import adminStyles from "../admin/Admin.module.css";
import styles from "./JoinTeamPanel.module.css";

/** The longest message the server accepts for a team's owner. */
const MaximumMessageLength = 500;

/** Two figures and a plus: the glyph on the tile that leads the panel. Decorative, so hidden from assistive technology. */
function TeamIcon(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19c.4-3.2 2.8-5 6-5s5.6 1.8 6 5" />
      <path d="M18 8v6M15 11h6" />
    </svg>
  );
}

/** The first letters of a team's name, shown on its badge once chosen. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => word !== "")
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");
}

/**
 * The "Ask to join a team" control on My Requests: a button that opens a short form. The person searches for a team by name (they
 * already know it, from a colleague), picks it, may add a message for its owner, and sends the request. The request is routed to the
 * team's owner, who approves it, adding the person to the team, or denies it with a message. Outcomes are reported as toasts.
 *
 * @param props.onRaised - Called after a request is raised, so the page can show it among the person's requests.
 */
export function JoinTeamPanel({
  onRaised,
}: {
  onRaised: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const baseId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState<TeamSearchResult | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const close = (): void => {
    setOpen(false);
    setSearch("");
    setTeam(null);
    setMessage("");
  };

  const find = useCallback(
    async (term: string): Promise<SearchSelectOutcome<TeamSearchResult>> => {
      if (term.length < MinimumTeamSearchLength) {
        return { kind: "ok", items: [] };
      }

      const result = await searchTeams(term);
      return result.kind === "ok"
        ? { kind: "ok", items: result.response }
        : {
            kind: "error",
            message:
              result.kind === "denied"
                ? t("joinTeam.searchDenied")
                : result.message,
          };
    },
    [t],
  );

  const send = async (): Promise<void> => {
    if (team === null) {
      return;
    }

    setSending(true);
    const result = await raiseJoinTeamRequest(
      team.groupId,
      message.trim() === "" ? null : message.trim(),
    );
    setSending(false);
    if (result.kind === "ok") {
      toast.show(t("joinTeam.sent", { name: team.name }), "success");
      close();
      onRaised();
    } else {
      toast.show(
        result.kind === "denied" ? t("joinTeam.sendDenied") : result.message,
        "error",
      );
    }
  };

  return (
    <section
      className={open ? `${styles.panel} ${styles.isOpen}` : styles.panel}
    >
      <h2 className={styles.heading}>
        <button
          type="button"
          className={styles.invite}
          aria-labelledby={`${baseId}-invite`}
          aria-expanded={open}
          aria-controls={`${baseId}-form`}
          aria-describedby={`${baseId}-teaser`}
          onClick={() => (open ? close() : setOpen(true))}
        >
          <span className={styles.tile}>
            <TeamIcon />
          </span>
          <span className={styles.inviteText}>
            <span id={`${baseId}-invite`} className={styles.inviteTitle}>
              {t("joinTeam.title")}
            </span>
            <span id={`${baseId}-teaser`} className={styles.teaser}>
              {t("joinTeam.teaser")}
            </span>
          </span>
          <svg
            className={styles.arrow}
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </h2>
      <div id={`${baseId}-form`} className={styles.drawer}>
        {open && (
          <div className={styles.form}>
            <p className={styles.help}>{t("joinTeam.help")}</p>
            {team === null ? (
              <SearchSelect
                label={t("joinTeam.searchLabel")}
                placeholder={t("joinTeam.searchPlaceholder")}
                value={search}
                onValueChange={setSearch}
                search={find}
                itemKey={(item) => item.groupId}
                itemLabel={(item) =>
                  item.context === null
                    ? item.name
                    : `${item.name} (${item.context})`
                }
                onSelect={(item) => {
                  if (item.isMember) {
                    toast.show(
                      t("joinTeam.alreadyMember", { name: item.name }),
                      "error",
                    );
                    return;
                  }

                  setTeam(item);
                }}
                onEscape={close}
                autoFocus
                className={styles.field}
              />
            ) : (
              <p className={styles.chosen}>
                <span className={styles.badge} aria-hidden="true">
                  {initials(team.name)}
                </span>
                <span className={styles.chosenName}>
                  <span className="visually-hidden">
                    {t("joinTeam.chosenLabel")}
                  </span>
                  <strong>{team.name}</strong>
                  {team.context !== null && (
                    <span className={styles.context}>{team.context}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => setTeam(null)}
                  disabled={sending}
                >
                  {t("joinTeam.change")}
                </button>
              </p>
            )}
            <div className={`${adminStyles.field} ${styles.messageField}`}>
              <label htmlFor={`${baseId}-message`}>
                {t("joinTeam.messageLabel")}
              </label>
              <textarea
                id={`${baseId}-message`}
                rows={3}
                maxLength={MaximumMessageLength}
                value={message}
                placeholder={t("joinTeam.messagePlaceholder")}
                onChange={(event) => setMessage(event.target.value)}
                disabled={sending}
              />
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                className="button button-primary"
                disabled={team === null || sending}
                onClick={() => void send()}
              >
                {t("joinTeam.send")}
              </button>
              <button
                type="button"
                className="button button-secondary"
                disabled={sending}
                onClick={close}
              >
                {t("joinTeam.cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
